/**
 * AssessmentsToday (#719) — the first-time dogs, on the dashboard, at 6:45 AM.
 *
 * Kylie (HTHD receptionist), 2026-07-29 walkthrough: "When dogs want to come
 * for assessments and meet-and-greets, it comes through to our email instead
 * of Gingr… if there was an option on here so I can go and see it right away
 * when I walk in in the morning." She pre-verifies vaccines before every
 * assessment, and the week before that call, records "were uploaded but not
 * entered" — so a name and a time are not enough. The row has to carry the
 * shot answer.
 *
 * ── THE EMPTY STATE IS THE PRIMARY STATE ────────────────────────────────
 * Assessments are DARK: no tenant has assessment capacity configured, so in
 * production this panel renders empty every single morning until someone runs
 * the config script. An empty panel is therefore the design, not the leftover
 * branch — and there are three distinct ways to be empty, which must never
 * look alike (Principle 5, the 2026-07-09 incident class):
 *
 *   dataSuspect          → "Today's schedule isn't in this system yet."
 *                          The server flagged today's zeros as structural.
 *                          Says nothing about assessments at all.
 *   !configured          → "Assessments aren't set up for this location yet."
 *                          The tenant does not offer them. A count of 0 here
 *                          is not a quiet morning — it is a feature that is
 *                          off, and saying "no assessments booked" would be a
 *                          lie by implication.
 *   configured, count 0  → "No assessments booked for today." A true zero.
 *
 * The header count follows the same rule: it renders "—" rather than a
 * confident 0 whenever the number is not trustworthy.
 *
 * ── WHAT THE CHIPS CLAIM ────────────────────────────────────────────────
 * `vaxVerification` is the authoritative answer, derived server-side from
 * `vaccinations` (expiry AND the staff `verified` sign-off — one derivation,
 * shared, so this panel and the compliance surface cannot disagree).
 *
 * `queuePending` is a WEAKER, secondary signal from #551's verification
 * queue: a record is sitting unreviewed whose matcher SUGGESTED this dog.
 * It is a suggestion, not a link — approval writes to whichever dog the
 * reviewer picks — so the chip says "may be this dog's" and never implies the
 * paperwork is handled. `null` means the queue could not be read and renders
 * as nothing at all, never as "clear".
 *
 * Design-Philosophy: #1 (one panel, one source), #2 (the row's next action is
 * the vax answer), #5 (three empty states, no fabricated zeros), #6 (tokens
 * only — structural `--ferro-` for status, `--brand-` for identity), #7
 * (44px targets, stacks to one column at 640px).
 */
import { useTranslation } from 'react-i18next';
import { DogFlagChips } from '../dog-first/index.js';
import { DogAvatar } from '../dog-first/DogAvatar.js';
import {
  formatTime,
  type AssessmentBooking,
  type AssessmentDog,
  type AssessmentsTodayResponse,
  type VaxVerificationLevel,
} from './dashboard-api.js';
import './assessments-today.css';

interface AssessmentsTodayProps {
  readonly data: AssessmentsTodayResponse | null;
  /** True only while the FIRST payload is loading (skeleton, never blank). */
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry?: () => void;
  /** Principle 5 — the server says today's zeros may be structural. */
  readonly dataSuspect?: boolean;
}

type ChipTone = 'ok' | 'warn' | 'danger' | 'info';

const VAX_CHIP: Record<
  VaxVerificationLevel,
  { label: string; tone: ChipTone }
> = {
  expired: { label: 'Expired', tone: 'danger' },
  missing: { label: 'No records', tone: 'danger' },
  unverified: { label: 'Needs verification', tone: 'warn' },
  // Ted #32a (E1.7) — from Gingr or typed at the desk, in date, unsigned.
  on_file: { label: 'On file', tone: 'info' },
  verified: { label: 'Verified', tone: 'ok' },
};

function VaxChip({ dog }: { dog: AssessmentDog }): JSX.Element {
  const { level, detail, queuePending } = dog.vaxVerification;
  // A level this build has never heard of reads as the cautious one.
  const chip = VAX_CHIP[level] ?? VAX_CHIP.unverified;
  return (
    <span className="ferro-assessment-row__chips">
      <span
        className={`ferro-assessment-chip ferro-assessment-chip--${chip.tone}`}
        title={detail}
        data-testid={`assessment-vax-${dog.dogId}`}
        data-level={level}
      >
        {chip.label}
      </span>
      {/*
        Secondary and deliberately weaker than the chip above it. Shown ONLY
        for `true`; `null` (queue unreadable) renders nothing, because an
        absent chip reads as "no information", while a chip saying "none
        pending" would be a claim we cannot support.
      */}
      {queuePending === true ? (
        <span
          className="ferro-assessment-chip ferro-assessment-chip--info"
          title="A vaccination record awaiting staff review may belong to this dog. The match is a suggestion — confirm it in the Vaccination Inbox."
          data-testid={`assessment-queue-${dog.dogId}`}
        >
          Record in inbox
        </span>
      ) : null}
    </span>
  );
}

function AssessmentRow({
  booking,
}: {
  booking: AssessmentBooking;
}): JSX.Element {
  const time = formatTime(booking.expectedTime);
  return (
    <li
      className={`ferro-assessment-row${
        booking.needsAttention ? ' ferro-assessment-row--attention' : ''
      }`}
      data-testid={`assessment-row-${booking.bookingId}`}
    >
      <span className="ferro-assessment-row__time">
        {time || <span className="ferro-assessment-row__time-unknown">Time not set</span>}
      </span>
      <div className="ferro-assessment-row__dogs">
        {booking.dogs.map((dog) => (
          <div className="ferro-assessment-row__dog" key={dog.dogId}>
            <DogAvatar name={dog.name} photoUrl={dog.photoUrl} size="sm" />
            <div className="ferro-assessment-row__dog-text">
              <span className="ferro-assessment-row__dog-name">{dog.name}</span>
              {dog.breed ? (
                <span className="ferro-assessment-row__breed">{dog.breed}</span>
              ) : null}
              {/* Ted #28 Phase 2 (#1050) — the registry's flags beside the
                  dog (INTACT, meds, transport…). The #719 readiness chip
                  below answers a different question — "may this dog walk in
                  for its assessment?" — and stays. */}
              <span className="ferro-assessment-row__flags">
                <DogFlagChips flags={dog.flags} scope="dog" testIdPrefix={`assessment-${dog.dogId}-`} />
              </span>
            </div>
            <VaxChip dog={dog} />
          </div>
        ))}
      </div>
      <span className="ferro-assessment-row__owner">
        {/*
          #537 — a floor role gets the dog and its shot status but not the
          owner's identity. Say so: a blank owner cell is indistinguishable
          from a booking with no customer attached.
        */}
        {booking.customerVisible ? (
          <>
            {booking.customerName ?? 'Unknown customer'}
            {/* Ted #28 Phase 2 — the owner's flags under the owner. */}
            <DogFlagChips flags={booking.dogs[0]?.flags} scope="account" testIdPrefix={`assessment-owner-${booking.bookingId}-`} />
          </>
        ) : (
          <span
            className="ferro-assessment-row__owner-hidden"
            data-testid={`assessment-owner-hidden-${booking.bookingId}`}
          >
            Owner hidden for your role
          </span>
        )}
      </span>
    </li>
  );
}

/** The three ways this panel is legitimately empty. They must not look alike. */
function EmptyState({
  dataSuspect,
  configured,
}: {
  dataSuspect: boolean;
  configured: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  if (dataSuspect) {
    return (
      <p
        className="ferro-assessments__empty ferro-assessments__empty--stale"
        data-testid="assessments-empty-stale"
      >
        {t('admin.dashboard.hero.scheduleNotInSystem')}
      </p>
    );
  }
  if (!configured) {
    return (
      <div
        className="ferro-assessments__empty ferro-assessments__empty--unconfigured"
        data-testid="assessments-empty-unconfigured"
      >
        <p className="ferro-assessments__empty-title">
          Assessments aren’t set up for this location yet.
        </p>
        <p className="ferro-assessments__empty-help">
          Once assessment days and times are configured, first-time dogs booked
          for today will appear here with their vaccination status.
        </p>
      </div>
    );
  }
  return (
    <p
      className="ferro-assessments__empty"
      data-testid="assessments-empty-none-today"
    >
      No assessments booked for today.
    </p>
  );
}

export function AssessmentsToday({
  data,
  isLoading,
  error,
  onRetry,
  dataSuspect = false,
}: AssessmentsTodayProps): JSX.Element {
  const assessments = data?.assessments ?? [];
  const configured = data?.configured ?? false;
  // Principle 5 — never a confident 0. The count is unknown while the first
  // payload is in flight, when the fetch failed, and when the server has told
  // us today's zeros are structural.
  const countUnknown = data === null || (dataSuspect && assessments.length === 0);

  return (
    <article
      className="ferro-dashboard-widget ferro-assessments"
      aria-label="Assessments today"
      data-testid="assessments-today"
    >
      <header className="ferro-dashboard-widget__header">
        <h2 className="ferro-section__subtitle">Assessments today</h2>
        <span
          className={`ferro-assessments__count${
            countUnknown ? ' ferro-assessments__count--unknown' : ''
          }`}
          data-testid="assessments-count"
        >
          {countUnknown ? '—' : assessments.length}
        </span>
        {isLoading && (
          <div
            className="ferro-dashboard-widget__spinner"
            role="status"
            aria-label="Loading today's assessments"
          />
        )}
      </header>

      {error && (
        <div className="ferro-dashboard-widget__error" role="alert">
          <span>Couldn’t load today’s assessments.</span>
          {onRetry ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onRetry}
            >
              Retry
            </button>
          ) : null}
        </div>
      )}

      {!data && isLoading && (
        <div
          className="ferro-assessments__skeleton"
          data-testid="assessments-skeleton"
          aria-hidden="true"
        >
          <div className="ferro-assessments__skeleton-row" />
          <div className="ferro-assessments__skeleton-row" />
        </div>
      )}

      {data && assessments.length === 0 && (
        <EmptyState dataSuspect={dataSuspect} configured={configured} />
      )}

      {data && assessments.length > 0 && (
        <>
          <ul className="ferro-assessments__list">
            {assessments.map((booking) => (
              <AssessmentRow key={booking.bookingId} booking={booking} />
            ))}
          </ul>
          {/*
            The queue probe failed. Say which claim we could NOT make rather
            than silently dropping the "Record in inbox" chips — otherwise a
            dog whose records are sitting unreviewed looks identical to one
            with nothing waiting.
          */}
          {data.queueStatus === 'unavailable' ? (
            <p
              className="ferro-assessments__caption"
              data-testid="assessments-queue-unavailable"
            >
              Couldn’t check the vaccination inbox — records may be waiting for
              review that aren’t shown here.
            </p>
          ) : null}
        </>
      )}
    </article>
  );
}
